import React, { useMemo } from 'react';
import { IMessage, IMessagesQueryParams } from '@ui/components';
import { ContextUserType, useMessageContext } from '@ui/components';
import { twMerge } from 'tailwind-merge';
import { PostCommentReactionPanel } from './post-comment-reaction-panel';
import { sendGAEvent } from '@next/third-parties/google';
import { formatFansNumber } from '@ui/lib';
import { useInteractionGuard } from '@hooks/index';

interface IProps {
  message: IMessage;
  contextUserType: ContextUserType;
  query: IMessagesQueryParams;
  triggerScoreAnimation: (value: number) => void;
}

export const PostCommentReactions: React.FC<IProps> = React.memo(
  ({ message, contextUserType, query, triggerScoreAnimation }) => {
    const messageState = useMessageContext(contextUserType);
    const { interceptInteraction } = useInteractionGuard();

    const joinMessage = useMemo(() => {
      const creatorDisplayName =
        (messageState as any)?.community_details?.creator_display_name ||
        (message as any)?.community_details?.creator_display_name ||
        (message as any)?.creator_display_name;
      return creatorDisplayName
        ? `Join community to use this feature. Collect this badge to join community!`
        : 'Join this KOR Space to use this feature. Collect this badge to join.';
    }, [messageState, message]);

    const onReactToMessage = interceptInteraction((event: any, emoji: string) => {
      event.preventDefault();
      event.stopPropagation();

      const userReactions = message?.user_reactions?.[messageState.user_id] || [];
      const isRemoving = userReactions.includes(emoji);
      messageState.onReactToMessage(message.id, message.slug, 'reaction', emoji, query);
      // Send GA events
      sendGAEvent('event', 'UserReaction', {
        event: 'emoji_reaction',
        event_action: isRemoving ? 'Remove Emoji' : 'Add Emoji',
        event_label: emoji,
        post_id: message.id,
        user_type: contextUserType,
      });

      !isRemoving && triggerScoreAnimation(0.5);
    }, joinMessage);

    const reactionList = useMemo(() => {
      if (!message?.reaction || !message?.reaction?.reactions_list) {
        return null;
      }

      const userReactions = message.user_reactions?.[messageState.user_id] || [];

      return Object.entries(message.reaction.reactions_list).map(
        ([emoji, count]) =>
          count > 0 && (
            <div
              onClick={(e) => onReactToMessage(e, emoji)}
              key={emoji}
              className={`bg-input flex h-fit w-fit min-w-[50px] cursor-pointer items-center justify-center space-x-1 rounded-lg border py-[1px] px-[3px] text-xs hover:bg-black/20 ${
                userReactions.includes(emoji) ? 'border-orange-200 bg-orange-100' : ''
              }`}>
              <span className="text-sm sm:text-lg">{emoji}</span>
              {<span className="ml-1 text-xs font-medium">{formatFansNumber(count)}</span>}
            </div>
          ),
      );
    }, [message.reaction, message.user_reactions, messageState.user_id]);

    const showReactionPanel = useMemo(() => {
      // Always show if we want the user to be able to add the first reaction
      return true;
    }, []);

    return (
      <div className={twMerge('flex flex-wrap gap-x-2 gap-y-2 sm:gap-y-0')}>
        {reactionList}
        <PostCommentReactionPanel
          message={message}
          contextUserType={contextUserType}
          query={query || messageState.query}
          triggerScoreAnimation={() => triggerScoreAnimation(0.5)}
        />
      </div>
    );
  },
);

PostCommentReactions.displayName = 'PostCommentReactions';
